import React, { useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import MobileStepper from "@mui/material/MobileStepper";
import Button from "@mui/material/Button";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Tutorial Wrapper
import TutorialStep from "../TutorialStep";

// Images (Placeholders will be used until user uploads)
import hubWelcome from "assets/images/tutorial/insighthub-welcome.png";
import hubWorkflow from "assets/images/tutorial/insighthub-workflow.png";
import hubData from "assets/images/tutorial/insighthub-data.png";

function InsightHubIntroStep({ open, onComplete, onSkip, showWorkflowStep = true }) {
    const [activeStep, setActiveStep] = useState(0);

    const workflowStep = {
        label: "The Workflow",
        description: "To generate a report, first go to Social Stash, select your profiles, and click 'Analyse Profiles'.",
        image: hubWorkflow,
    };

    const commonSteps = [
        {
            label: "Report Tools & Controls",
            description: "Filter charts by category, select custom date ranges, and use tools to edit, share, or download your detailed reports.",
            image: hubWelcome,
        },
        {
            label: "Understanding Data",
            description: "View detailed growth, engagement, and predictive trends for your selected profiles.",
            image: hubData,
        },
    ];

    // If showWorkflowStep is true, put it first. Otherwise just show common steps.
    const steps = showWorkflowStep ? [workflowStep, ...commonSteps] : commonSteps;
    const maxSteps = steps.length;

    const handleNext = () => {
        if (activeStep === maxSteps - 1) {
            onComplete(); // Finish on last step
        } else {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    return (
        <TutorialStep open={open} onClose={onSkip} maxWidth="lg">
            <MDBox p={2} display="flex" flexDirection="column" alignItems="center" textAlign="center">

                {/* Image Card with Header */}
                <MDBox
                    mb={2}
                    mt={-2}
                    p={0} // No padding on outer container to let header be flush
                    bgColor="grey-200"
                    borderRadius="xl"
                    display="flex"
                    flexDirection="column"
                    sx={{ width: "100%", height: "400px", overflow: "hidden" }}
                >
                    {/* Header Bar */}
                    <MDBox
                        width="100%"
                        bgColor="info"
                        py={1.5}
                        textAlign="center"
                        style={{ borderRadius: "12px 12px 0 0" }} // Explicit top rounding
                    >
                        <MDTypography variant="subtitle1" color="white" fontWeight="medium" sx={{ lineHeight: 1, m: 0 }}>
                            How to use Insight Hub
                        </MDTypography>
                    </MDBox>

                    {/* Image Area */}
                    <MDBox
                        flexGrow={1}
                        width="100%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        p={2}
                    >
                        {/* Using text fallback if image fails to load during development */}
                        <MDBox
                            component="img"
                            src={steps[activeStep].image}
                            alt={steps[activeStep].label}
                            maxWidth="100%"
                            maxHeight="100%"
                            sx={{ objectFit: "contain" }}
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                        />
                        <MDBox display="none">
                            <MDTypography variant="caption">Image: {steps[activeStep].label}</MDTypography>
                        </MDBox>
                    </MDBox>
                </MDBox>

                {/* content */}
                <MDTypography variant="h4" gutterBottom fontWeight="bold">
                    {steps[activeStep].label}
                </MDTypography>
                <MDTypography variant="body2" color="text" sx={{ maxWidth: "80%", mb: 4 }}>
                    {steps[activeStep].description}
                </MDTypography>

                {/* Stepper Dots & Navigation */}
                <MDBox width="100%" display="flex" flexDirection="column" alignItems="center">
                    <MobileStepper
                        variant="dots"
                        steps={maxSteps}
                        position="static"
                        activeStep={activeStep}
                        sx={{
                            maxWidth: 400,
                            flexGrow: 1,
                            bgcolor: "grey.200",
                            borderRadius: "12px",
                            mb: 2,
                            p: 1,
                            justifyContent: "center"
                        }}
                        backButton={null}
                        nextButton={null}
                    />

                    <MDBox display="flex" justifyContent="space-between" alignItems="center" width="100%" mt={2}>
                        <MDButton
                            variant="text"
                            color="secondary"
                            onClick={handleBack}
                            disabled={activeStep === 0}
                            startIcon={<KeyboardArrowLeft />}
                        >
                            Back
                        </MDButton>

                        <MDButton onClick={onComplete} variant="text" color="secondary" size="small">
                            Skip Tutorial
                        </MDButton>

                        <MDButton
                            variant="contained"
                            color="accent"
                            onClick={handleNext}
                            endIcon={activeStep === maxSteps - 1 ? undefined : <KeyboardArrowRight />}
                        >
                            {activeStep === maxSteps - 1 ? "Get Started" : "Next"}
                        </MDButton>
                    </MDBox>
                </MDBox>
            </MDBox>
        </TutorialStep>
    );
}

InsightHubIntroStep.propTypes = {
    open: PropTypes.bool.isRequired,
    onComplete: PropTypes.func.isRequired,
    onSkip: PropTypes.func.isRequired,
    showWorkflowStep: PropTypes.bool,
};

// InsightHubIntroStep.defaultProps = {
//     showWorkflowStep: true,
// };

export default InsightHubIntroStep;
